/*
 * The "as of" element, with its two calibrations (§5, §10, §11).
 *
 * STOPPED LOOKS STOPPED (principle 5). Staleness is always visible, always
 * TEXTUAL, never colour-only. The colour is a reinforcement of a sentence that
 * already says it, which is what keeps this readable in forced-colors mode and
 * to a colour-blind user without a second code path.
 *
 * THE ELEMENT NEVER DIMS ITSELF. §6: "dim the datum, never the words". This is
 * the words. In the day-of regime it goes AMBER as it ages, which is the
 * opposite of receding — the explanatory text and controls stay at full contrast
 * precisely because the row's datum is the thing losing confidence.
 *
 * A <time> element, so the machine-readable instant travels with the human
 * phrase. That is what lets a screen reader or a future scraper get the exact
 * observation without parsing "14 min ago".
 */

import { useTranslation } from "react-i18next";

import { elapsedKey, minutesSince, stalenessTier, type Regime } from "../lib/staleness";
import styles from "./AsOf.module.css";

export interface AsOfProps {
  /** The observation instant, UTC from the server. */
  observedAt: string;

  /**
   * Which calibration to use.
   *
   * "planning" phrases it as reassurance ("Checked 3 hours ago") and never
   * ambers. "day-of" phrases it as a reading ("as of 14 min ago") and ambers
   * past the aging threshold.
   */
  regime: Regime;

  /** For tests and stories; defaults to now. */
  now?: Date;

  /**
   * False keeps the day-of reading out of amber whatever its age: before an
   * event's start nothing is owed, so an old reading is not a stale one
   * (decision 784). The age is still stated.
   */
  mayAge?: boolean;
}

export function AsOf({ observedAt, regime, now, mayAge = true }: AsOfProps) {
  const { t } = useTranslation();

  const minutes = minutesSince(observedAt, now);
  const { key, count } = elapsedKey(minutes);
  const ago = t(key, { count });

  const tier = stalenessTier(observedAt, regime, now);

  // Amber only in the day-of regime, and only once aging. §10 forbids amber in
  // the planning regime outright, so this cannot be a shared "is it old"
  // computation.
  const amber = mayAge && regime === "day-of" && tier !== "fresh";

  const label =
    regime === "planning"
      ? t("asOf.planningChecked", { ago })
      : t("asOf.dayOfAsOf", { ago });

  return (
    <time
      className={`${styles.asOf} ${amber ? styles.amber : ""}`}
      // The machine-readable instant. Always the raw UTC value, never the
      // formatted phrase.
      dateTime={observedAt}
    >
      {label}
    </time>
  );
}
